import * as React from "react";
import { useFlowFSStore } from "../state/flowfs-store";
import { useShallow } from "zustand/react/shallow";
import { useFlowFSRead, useFlowFSStat, useDelete, useMove } from "../hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { toast } from "sonner";
import { downloadFlowFS } from "../api/flowfs";

export default function DetailsPanel({ tenantId, flowId }: { tenantId: string; flowId: string }) {
  const { sel, previewAs, setPreviewAs } = useFlowFSStore(
    useShallow((s) => ({ sel: s.selection, previewAs: s.previewAs, setPreviewAs: s.setPreviewAs }))
  );
  const [downloadUrl, setDownloadUrl] = React.useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [dst, setDst] = React.useState("");
  const [overwrite, setOverwrite] = React.useState(false);

  React.useEffect(() => () => {
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
  }, [downloadUrl]);

  // Always call hooks; gate by passing undefined when not applicable
  const { data: stat } = useFlowFSStat(tenantId, flowId, sel?.path);
  const { data: read, isLoading: reading, error: readErr } =
    (useFlowFSRead(
      tenantId,
      flowId,
      sel?.type === "file" ? sel.path : undefined,
      previewAs
    ) as any);

  const del = useDelete(tenantId, flowId);
  const mv = useMove(tenantId, flowId);

  React.useEffect(() => {
    setDst(sel?.path ?? "");
  }, [sel?.path]);

  if (!sel) return <div className="p-3 text-sm text-muted-foreground">Select a file to preview</div>;

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 border-b">
        <div className="text-sm font-medium break-all">{sel.path}</div>
        {stat?.etag && (
          <div className="text-xs text-muted-foreground">
            ETag: <span className="font-mono">{stat.etag}</span>
          </div>
        )}
      </div>
      {sel.type === "file" && (
        <div className="p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Label className="text-xs">Preview</Label>
            <Button size="sm" variant={previewAs === "json" ? "default" : "outline"} onClick={() => setPreviewAs("json")}>
              JSON
            </Button>
            <Button size="sm" variant={previewAs === "text" ? "default" : "outline"} onClick={() => setPreviewAs("text")}>
              Text
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  const r = await downloadFlowFS({ tenantId, flowId, path: sel.path });
                  if (downloadUrl) URL.revokeObjectURL(downloadUrl);
                  setDownloadUrl(r.url);
                  const a = document.createElement("a");
                  a.href = r.url;
                  a.download = sel.path.split("/").pop() || "download";
                  a.click();
                } catch (e: any) {
                  toast.error(e?.message || "Download failed");
                }
              }}
            >
              Download
            </Button>
          </div>
          <div className="border rounded bg-muted/20 p-2 min-h-[200px]">
            {reading ? (
              <div className="text-sm">Loading preview…</div>
            ) : readErr ? (
              <div className="text-red-600 text-sm">{(readErr as any)?.message || "Failed to read"}</div>
            ) : (
              <pre className="text-xs font-mono whitespace-pre-wrap break-words">
                {previewAs === "json" ? JSON.stringify((read as any)?.data, null, 2) : String((read as any)?.data ?? "")}
              </pre>
            )}
          </div>
        </div>
      )}

      <div className="mt-auto p-3 border-t space-y-3">
        <div className="grid gap-1">
          <Label className="text-xs">Rename / Move (absolute dst)</Label>
          <div className="flex gap-2">
            <Input value={dst} onChange={(e) => setDst(e.target.value)} />
            <Button
              size="sm"
              onClick={async () => {
                try {
                  await mv.mutateAsync({ src: sel.path, dst, overwrite });
                } catch {}
              }}
            >
              Apply
            </Button>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
            Overwrite if exists
          </label>
        </div>
        <div className="flex justify-between">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              navigator.clipboard?.writeText(sel.path);
              toast.success("Path copied");
            }}
          >
            Copy Path
          </Button>
          <Button size="sm" variant="destructive" onClick={() => setConfirmOpen(true)}>
            Delete
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete file?"
        description="This cannot be undone."
        confirmText="Delete"
        variant="destructive"
        onConfirm={async () => {
          try {
            await del.mutateAsync({ path: sel.path });
          } catch {}
          setConfirmOpen(false);
        }}
      />
    </div>
  );
}
